import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import PageLoader from '../../../components/other/PageLoader';
import { Breadcrumbs } from '../../../components/library/Breadcrumbs';
import ErrorModal from '../../../components/library/error-modal';
import { useAuth } from '../../../container/authContext';
import { EmptyPage } from '../../../components/other/EmptyPage';
import { usePermission } from '../../../container/permissionContext';
import { useDebounce } from 'use-debounce';
import Layout from '../../../layout/default';
import { deleteTaskFolderService, getTasksExplorerService } from '../../../services/tasks';
import { isResponseOk } from '../../../utility/isResponseOk';
import ManageTaskModal from '../components/modals/manage-task-modal';
import ManageFolderModal from '../components/modals/manage-folder-modal';
import TaskFolderItem from '../components/TaskFolderItem';
import TaskListItem from '../components/TaskListItem';
import TaskFormItem from '../components/TaskFormItem';
import FormManagementModal from '../../../components/library/form-management-modal';
import './styles.scss';
import _ from 'lodash';

const ROOT_BREADCRUMB = { name: 'Tasks', url: '/tasks' };

// The explorer answers with the breadcrumbs nested from the root folder down;
// the trail is that chain flattened.
const flattenBreadcrumbs = (breadcrumbs) => {
  const trail = [];
  let current = breadcrumbs;

  while (current) {
    trail.push(current);
    current = current.children;
  }

  return trail;
};

const TasksPage = () => {
  const { user = {} } = useAuth();
  // The folder being browsed lives in the URL, so that "Tasks" in the sidebar
  // (which points at the bare /tasks) walks back out to the root, and so that a
  // level survives a reload and the browser's back button.
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin, isCompanySystemAdministrator } = usePermission();
  const { company: { id: companyID } = {} } = user;

  const [filters, setFilters] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [folders, setFolders] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [forms, setForms] = useState([]);
  const [breadcrumbs, setBreadcrumbs] = useState([ROOT_BREADCRUMB]);
  const [manageTaskModalData, setManageTaskModalData] = useState(null);
  const [manageFolderModalData, setManageFolderModalData] = useState(null);
  const [formManagementModalData, setFormManagementModalData] = useState(null);
  const [isDeleteFolderErrorModal, setIsDeleteFolderErrorModal] = useState(false);
  const [isMoveErrorModal, setIsMoveErrorModal] = useState(false);

  const [debouncedSearch] = useDebounce(filters.search, 300);

  const folderParam = searchParams.get('folder');
  const currentFolderID = folderParam ? Number(folderParam) : null;

  // Read as booleans, not as the context functions themselves: the loader below
  // is rebuilt whenever they change, and a function identity changing on every
  // render of the provider would make it refetch on every render.
  const isSuperAdminUser = isSuperAdmin();
  const hasWritePermissions = isSuperAdminUser || isCompanySystemAdministrator();
  const isSearching = !!debouncedSearch?.length;

  const onFilter = (value, key) => {
    setFilters((prevState) => ({
      ...prevState,
      [key]: value,
    }));
  };

  const handleGetTasks = useCallback(
    async (folderID) => {
      try {
        setIsLoading(true);

        const params = {};

        if (debouncedSearch) {
          params.search = debouncedSearch;
        }

        if (folderID) {
          params.folder_id = folderID;
        }

        if (isSuperAdminUser) {
          params.company_id = companyID;
        }

        const response = await getTasksExplorerService(params);
        if (isResponseOk(response) && response.data) {
          const { breadcrumbs: responseBreadcrumbs, data } = response.data;

          setFolders(data.filter((item) => item.type === 'folder'));
          setTasks(data.filter((item) => item.type === 'task'));
          setForms(data.filter((item) => item.type === 'form'));
          setBreadcrumbs([ROOT_BREADCRUMB, ...flattenBreadcrumbs(responseBreadcrumbs)]);
        }

        setIsLoading(false);
      } catch (e) {
        console.error(e);
        setIsLoading(false);
      }
    },
    [debouncedSearch, isSuperAdminUser, companyID],
  );

  useEffect(() => {
    // A search looks across every folder, so it always starts from the level the
    // user is on and gets cleared back into it.
    handleGetTasks(currentFolderID);
  }, [handleGetTasks, currentFolderID]);

  // Walking into a level only rewrites the URL; the effect above does the loading.
  const changeFolder = (folderID) => setSearchParams(folderID ? { folder: String(folderID) } : {});

  // Reloading the level the user is already on cannot go through the URL, as it
  // would not change.
  const reloadCurrentFolder = () => handleGetTasks(currentFolderID);

  const goToLevel = (id, type) => {
    if (type === 'folder') {
      changeFolder(id);
    } else {
      changeFolder(null);
    }
  };

  const handleEditTaskSuccess = (data) => {
    setTasks((prevState) => {
      const newState = _.cloneDeep(prevState);
      newState.map((stateTask) => {
        if (stateTask.id === data.id) {
          stateTask.name = data.name;
          stateTask.task_forms = data.task_forms;
          stateTask.forms_count = data.forms_count;
        }

        return stateTask;
      });

      return newState;
    });
  };

  const handleAddTaskSuccess = () => reloadCurrentFolder();

  const handleDeleteFolder = (folder) => {
    deleteTaskFolderService(folder.id)
      .then(() => reloadCurrentFolder())
      .catch((e) => {
        console.log(e);
        setIsDeleteFolderErrorModal(true);
      });
  };

  // A folder, a task and a form are all moved out of the level they are shown on,
  // so the level is reloaded rather than patched.
  const handleMove = () => reloadCurrentFolder();

  return (
    <Layout title="Tasks" className={'tasks-page-layout'}>
      <DndProvider backend={HTML5Backend}>
        <div className="page-wrapper library library-tasks">
          <Breadcrumbs breadcrumbs={breadcrumbs} goToLevel={goToLevel} />

          <div className="library-filter">
            <div className="library-filter-search form-control-item">
              <input
                type="text"
                className="form-control"
                value={filters.search ?? ''}
                onChange={(e) => onFilter(e.target.value, 'search')}
                placeholder="Search"
              />
              <i className="icon-search"></i>
            </div>
            {hasWritePermissions && (
              <div className="tasks-page-actions">
                <button className="btn btn-outline" onClick={() => setManageFolderModalData({})}>
                  <i className="icon-icon-folder mr-2"></i> Create Folder
                </button>
                <button className="btn" onClick={() => setManageTaskModalData({ isOpen: true })}>
                  <i className="icon-plus-svgrepo-com mr-2"></i> Add Task
                </button>
              </div>
            )}
          </div>

          {!!folders.length && (
            <>
              <h3 className="library-subtitle">Folders</h3>
              <div className="tasks-wrapper tasks-folders-wrapper">
                {folders.map((folder) => (
                  <TaskFolderItem
                    key={folder.id}
                    folder={folder}
                    hasWritePermissions={hasWritePermissions}
                    changeFolder={changeFolder}
                    handleEdit={(item) => setManageFolderModalData(item)}
                    handleDelete={handleDeleteFolder}
                    handleMove={handleMove}
                    handleMoveError={setIsMoveErrorModal}
                  />
                ))}
              </div>
            </>
          )}

          {!!tasks.length && (
            <>
              <h3 className="library-subtitle mt-2">Tasks</h3>
              <div className="tasks-wrapper">
                {tasks.map((task) => (
                  <TaskListItem
                    key={task.id}
                    task={task}
                    setManageTaskModalData={setManageTaskModalData}
                    setTasks={setTasks}
                    hasWritePermissions={hasWritePermissions}
                    handleMove={handleMove}
                    handleMoveError={setIsMoveErrorModal}
                  />
                ))}
              </div>
            </>
          )}

          {!!forms.length && (
            <>
              <h3 className="library-subtitle mt-2">Forms</h3>
              <div className="tasks-wrapper">
                {forms.map((form) => (
                  <TaskFormItem
                    key={form.id}
                    form={form}
                    hasWritePermissions={hasWritePermissions}
                    handleMove={handleMove}
                    handleMoveError={setIsMoveErrorModal}
                    openFormManagementModal={setFormManagementModalData}
                  />
                ))}
              </div>
            </>
          )}

          {!tasks.length && !folders.length && !forms.length && !isLoading && (
            <EmptyPage title={isSearching ? 'Unfortunately, nothing was found for your query' : 'Currently, there are no tasks'} />
          )}
        </div>
      </DndProvider>
      {isLoading && <PageLoader />}

      {manageTaskModalData && (
        <ManageTaskModal
          taskData={manageTaskModalData}
          handleCloseModal={() => setManageTaskModalData(null)}
          handleSuccess={manageTaskModalData.id ? handleEditTaskSuccess : handleAddTaskSuccess}
          companyId={companyID}
          folderId={currentFolderID}
        />
      )}

      {manageFolderModalData && (
        <ManageFolderModal
          folderData={manageFolderModalData}
          closeModal={() => setManageFolderModalData(null)}
          handleSuccess={() => reloadCurrentFolder()}
          companyId={companyID}
          parentId={currentFolderID}
        />
      )}

      <FormManagementModal form={formManagementModalData} onClose={() => setFormManagementModalData(null)} />

      <ErrorModal
        isOpen={isDeleteFolderErrorModal}
        closeModal={() => setIsDeleteFolderErrorModal(false)}
        title={'Empty Folder Required for Deletion'}
        subtitle={
          'You can only delete folders when they are empty. Please remove all items from this folder before proceeding with the deletion.'
        }
      />

      <ErrorModal
        isOpen={isMoveErrorModal}
        closeModal={() => setIsMoveErrorModal(false)}
        title={'Parent cannot be changed.'}
        subtitle={''}
      />
    </Layout>
  );
};

export default TasksPage;
